<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import GalleryCard from '@/components/organizer/galleries/GalleryCard.vue'
import GalleryEmptyState from '@/components/organizer/galleries/GalleryEmptyState.vue'
import GalleryFormSlideOver from '@/components/organizer/galleries/GalleryFormSlideOver.vue'
import GalleryStorageBanner from '@/components/organizer/galleries/GalleryStorageBanner.vue'
import GalleryStorageMeter from '@/components/organizer/galleries/GalleryStorageMeter.vue'
import { useEvents } from '@/composables/useEvents'
import { useGalleries } from '@/composables/useGalleries'
import { useGalleryStorage } from '@/composables/useGalleryStorage'
import { useGridReorder } from '@/composables/useGridReorder'
import { useGalleryUploadManager } from '@/composables/useGalleryUploadManager'
import { GalleryStatus, GalleryType } from '@/types/galleries'
import type { OrganizerGallery } from '@/types/galleries'
import { formatBytes } from '@/utils/formatBytes'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const eventSlug = route.params['eventSlug'] as string

const { event, fetchEvent } = useEvents()
const { galleries, loading, error, fetch, update, remove, saving, reorder, move } = useGalleries(eventSlug)
const storage = useGalleryStorage(eventSlug)

const panelOpen = ref(false)
const editingGallery = ref<OrganizerGallery | null>(null)
const deleteTarget = ref<OrganizerGallery | null>(null)
const deleting = ref(false)

const hasGalleries = computed(() => galleries.value.length > 0)

const reorderer = useGridReorder({
  getIds: () => galleries.value.map((g) => g.id),
  getColumns: () => (window.innerWidth >= 1280 ? 4 : window.innerWidth >= 768 ? 3 : window.innerWidth >= 640 ? 2 : 1),
  onReorder: (ids) => reorder(ids),
  labels: {
    lifted: (position, total) => t('galleries.reorder.lifted', { position, total }),
    moved: (position, total) => t('galleries.reorder.moved', { position, total }),
    dropped: (position, total) => t('galleries.reorder.dropped', { position, total }),
    cancelled: () => t('galleries.reorder.cancelled'),
  },
})

// Uploads continue in the background (tray): keep the cards and the meter current while they finish.
const manager = useGalleryUploadManager()
let refreshTimer: ReturnType<typeof setTimeout> | null = null
const offCreated = manager.onItemCreated((entry) => {
  if (entry.eventSlug !== eventSlug) return
  if (refreshTimer !== null) clearTimeout(refreshTimer)
  refreshTimer = setTimeout(() => {
    void fetch()
    void storage.fetch()
  }, 1500)
})
onBeforeUnmount(() => {
  offCreated()
  if (refreshTimer !== null) clearTimeout(refreshTimer)
})

onMounted(() => {
  void fetchEvent(eventSlug)
  void fetch()
  void storage.fetch()
})

function detailRoute(gallery: OrganizerGallery): string {
  return `/organizer/events/${eventSlug}/galleries/${gallery.id}`
}

function openCreate(): void {
  editingGallery.value = null
  panelOpen.value = true
}

function openEdit(gallery: OrganizerGallery): void {
  editingGallery.value = gallery
  panelOpen.value = true
}

function closePanel(): void {
  panelOpen.value = false
}

function onCreated(gallery: OrganizerGallery): void {
  panelOpen.value = false
  void router.push(detailRoute(gallery))
}

function onUpdated(): void {
  panelOpen.value = false
  void fetch()
}

async function toggleStatus(gallery: OrganizerGallery): Promise<void> {
  const next = gallery.status === GalleryStatus.Published ? GalleryStatus.Draft : GalleryStatus.Published
  await update(gallery.id, { status: next })
}

async function confirmDelete(): Promise<void> {
  const target = deleteTarget.value
  if (target === null) return
  deleting.value = true
  const ok = await remove(target.id)
  deleting.value = false
  if (ok) {
    deleteTarget.value = null
    void storage.fetch()
  }
}

function cancelDelete(): void {
  if (!deleting.value) deleteTarget.value = null
}

const deleteBody = computed(() =>
  deleteTarget.value === null
    ? ''
    : deleteTarget.value.type === GalleryType.External
      ? t('galleries.delete_external_confirm', { title: deleteTarget.value.title })
      : t('galleries.confirm.delete_gallery_body', {
        title: deleteTarget.value.title,
        count: deleteTarget.value.item_count,
        size: formatBytes(deleteTarget.value.bytes_used),
      }),
)
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">...</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">{{ t('galleries.heading') }}</span>
      </nav>

      <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-white">
            <template v-if="event">{{ event.name }}</template>
            <span v-else class="text-slate-500">{{ t('galleries.heading') }}</span>
          </h1>
          <p class="text-slate-400 mt-1">{{ t('galleries.subtitle') }}</p>
        </div>
        <button
          v-if="hasGalleries"
          type="button"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
          @click="openCreate"
        >
          {{ t('galleries.create') }}
        </button>
      </div>

      <div v-if="storage.summary.value" class="mb-6 space-y-4">
        <GalleryStorageMeter :summary="storage.summary.value" />
        <GalleryStorageBanner :summary="storage.summary.value" />
      </div>

      <!-- Loading -->
      <div v-if="loading && !hasGalleries" class="flex justify-center py-20" role="status" :aria-label="t('common.loading')">
        <AppSpinner />
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-8 text-center"
        role="alert"
      >
        <p class="text-sm text-red-300">{{ t('galleries.errors.load_failed') }}</p>
        <button
          type="button"
          class="mt-4 border border-red-400/50 text-red-200 hover:bg-red-500/10 font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="fetch"
        >
          {{ t('galleries.errors.retry') }}
        </button>
      </div>

      <!-- Empty -->
      <GalleryEmptyState
        v-else-if="!hasGalleries"
        variant="no-galleries"
        :quota="storage.summary.value ? formatBytes(storage.summary.value.quota_bytes) : undefined"
        @create="openCreate"
      />

      <!-- Cards -->
      <ul
        v-else
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5"
        :aria-label="t('galleries.heading')"
        data-testid="gallery-card-list"
      >
        <GalleryCard
          v-for="(gallery, index) in galleries"
          :key="gallery.id"
          :gallery="gallery"
          :to="detailRoute(gallery)"
          :position="index + 1"
          :total="galleries.length"
          :dragging="reorderer.draggingId.value === gallery.id"
          :drag-over="reorderer.dragOverId.value === gallery.id && reorderer.draggingId.value !== gallery.id"
          :lifted="reorderer.liftedId.value === gallery.id"
          @edit="openEdit(gallery)"
          @toggle-status="toggleStatus(gallery)"
          @move-to-start="move(gallery.id, 'start')"
          @move-to-end="move(gallery.id, 'end')"
          @delete="deleteTarget = gallery"
          @drag-start="(e) => reorderer.onDragStart(gallery.id, e)"
          @drag-over="(e) => reorderer.onDragOver(gallery.id, e)"
          @drop="(e) => reorderer.onDrop(gallery.id, e)"
          @drag-end="reorderer.onDragEnd()"
          @handle-keydown="(e) => reorderer.onHandleKeydown(gallery.id, e)"
        />
      </ul>

      <p class="sr-only" aria-live="assertive">{{ reorderer.announcement.value }}</p>
    </main>

    <GalleryFormSlideOver
      :open="panelOpen"
      :event-slug="eventSlug"
      :gallery="editingGallery"
      @close="closePanel"
      @created="onCreated"
      @updated="onUpdated"
    />

    <ConfirmDialog
      :open="deleteTarget !== null"
      :title="t('galleries.confirm.delete_gallery_title')"
      :body="deleteBody"
      :confirm-label="t('galleries.card.delete')"
      :loading="deleting || saving"
      @update:open="cancelDelete"
      @confirm="confirmDelete"
    />
  </OrganizerLayout>
</template>
